import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import '../css/AdminDashboard.css';

function AdminDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [topFailedAttempts, setTopFailedAttempts] = useState([]);
  const [referralStats, setReferralStats] = useState([]);
  const [usersBelowThreshold, setUsersBelowThreshold] = useState([]);
  const [topSuccessful24h, setTopSuccessful24h] = useState([]);
  const [topSuccessful7d, setTopSuccessful7d] = useState([]);
  const [topSuccessfulAllTime, setTopSuccessfulAllTime] = useState([]);
  const [topFailed24h, setTopFailed24h] = useState([]);
  const [topFailed7d, setTopFailed7d] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch main dashboard data
        const dashboardResponse = await fetch('/api/auth/admin/dashboard-data', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!dashboardResponse.ok) {
          throw new Error('Failed to fetch admin dashboard data');
        }
        const dashboardResult = await dashboardResponse.json();
        setDashboardData(dashboardResult);
        
        // Fetch top failed attempts data
        const failedAttemptsResponse = await fetch('/api/auth/admin/top-failed-attempts', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!failedAttemptsResponse.ok) {
          throw new Error('Failed to fetch top failed attempts data');
        }
        const failedAttemptsResult = await failedAttemptsResponse.json();
        setTopFailedAttempts(failedAttemptsResult.users);
        
        // Fetch referral statistics data
        const referralStatsResponse = await fetch('/api/auth/admin/referral-stats', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!referralStatsResponse.ok) {
          throw new Error('Failed to fetch referral statistics data');
        }
        const referralStatsResult = await referralStatsResponse.json();
        setReferralStats(referralStatsResult.referralStats);
        
        // Fetch users below threshold data
        const usersBelowThresholdResponse = await fetch('/api/auth/admin/users-below-threshold', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!usersBelowThresholdResponse.ok) {
          throw new Error('Failed to fetch users below threshold data');
        }
        const usersBelowThresholdResult = await usersBelowThresholdResponse.json();
        setUsersBelowThreshold(usersBelowThresholdResult.users);
        
        // Fetch top successful lookups - 24 hours
        const topSuccessful24hResponse = await fetch('/api/auth/admin/top-successful-24h', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!topSuccessful24hResponse.ok) {
          throw new Error('Failed to fetch top successful lookups (24h) data');
        }
        const topSuccessful24hResult = await topSuccessful24hResponse.json();
        setTopSuccessful24h(topSuccessful24hResult.users);
        
        // Fetch top successful lookups - 7 days
        const topSuccessful7dResponse = await fetch('/api/auth/admin/top-successful-7d', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!topSuccessful7dResponse.ok) {
          throw new Error('Failed to fetch top successful lookups (7d) data');
        }
        const topSuccessful7dResult = await topSuccessful7dResponse.json();
        setTopSuccessful7d(topSuccessful7dResult.users);
        
        // Fetch top successful lookups - all time
        const topSuccessfulAllTimeResponse = await fetch('/api/auth/admin/top-successful-alltime', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!topSuccessfulAllTimeResponse.ok) {
          throw new Error('Failed to fetch top successful lookups (all-time) data');
        }
        const topSuccessfulAllTimeResult = await topSuccessfulAllTimeResponse.json();
        setTopSuccessfulAllTime(topSuccessfulAllTimeResult.users);
        
        // Fetch top failed lookups - 24 hours
        const topFailed24hResponse = await fetch('/api/auth/admin/top-failed-24h', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!topFailed24hResponse.ok) {
          throw new Error('Failed to fetch top failed lookups (24h) data');
        }
        const topFailed24hResult = await topFailed24hResponse.json();
        setTopFailed24h(topFailed24hResult.users);
        
        // Fetch top failed lookups - 7 days
        const topFailed7dResponse = await fetch('/api/auth/admin/top-failed-7d', {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        });
        if (!topFailed7dResponse.ok) {
          throw new Error('Failed to fetch top failed lookups (7d) data');
        }
        const topFailed7dResult = await topFailed7dResponse.json();
        setTopFailed7d(topFailed7dResult.users);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (user && user.token) {
      fetchData();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading admin dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  // Format date to be more readable
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format large numbers with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Welcome, {user.email}</p>
      </div>

      <h2 className="section-title">Usage Statistics</h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card highlight">
          <h3>All Time Successful Lookups</h3>
          <div className="stat-value">{formatNumber(dashboardData.allTimeSuccessfulLookups)}</div>
          <div className="stat-note">Tracking began 5/20/2025 -- historical estimates from Stripe</div>
        </div>
        
        <div className="admin-stat-card">
          <h3>Tracked Successful Lookups</h3>
          <div className="stat-value">{formatNumber(dashboardData.trackedSuccessfulLookups)}</div>
        </div>
        
        <div className="admin-stat-card">
          <h3>Tracked Failed Attempts</h3>
          <div className="stat-value">{formatNumber(dashboardData.trackedFailedAttempts)}</div>
        </div>
      </div>

      <h2 className="section-title">System Statistics</h2>
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <h3>HubSpot Connections</h3>
          <div className="stat-value">{dashboardData.hubspotConnectionsCount}</div>
        </div>
        
        <div className="admin-stat-card">
          <h3>Portal Configurations</h3>
          <div className="stat-value">{dashboardData.portalConfigsCount}</div>
        </div>
        
        <div className="admin-stat-card">
          <h3>Credit Notification Unsubscribes</h3>
          <div className="stat-value">{dashboardData.creditUnsubscribesCount}</div>
        </div>
        
        <div className="admin-stat-card">
          <h3>Portal Config Notification Unsubscribes</h3>
          <div className="stat-value">{dashboardData.portalConfigUnsubscribesCount}</div>
        </div>
      </div>

      <div className="admin-reports">
        <div className="admin-report-card">
          <h2>Recent Signups (Last 7 Days)</h2>
          {dashboardData.recentSignups.length === 0 ? (
            <p className="no-data">No signups in the last 7 days.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Credits</th>
                  <th>Signup Date</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.recentSignups.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.credits}</td>
                    <td>{formatDate(user.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="admin-report-card">
          <h2>Users with Credits Over 100</h2>
          {dashboardData.highCreditUsers.length === 0 ? (
            <p className="no-data">No users with more than 100 credits.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Credits</th>
                </tr>
              </thead>
              <tbody>
                {dashboardData.highCreditUsers.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.credits}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="admin-report-card">
          <h2>Top 5 Failed Attempt Users</h2>
          {topFailedAttempts.length === 0 ? (
            <p className="no-data">No users with failed attempts.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Failed Attempts</th>
                </tr>
              </thead>
              <tbody>
                {topFailedAttempts.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.failed_attempts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="admin-report-card">
          <h2>Referral Count</h2>
          {referralStats.length === 0 ? (
            <p className="no-data">No referral data available.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Referral Type</th>
                  <th>User Count</th>
                </tr>
              </thead>
              <tbody>
                {referralStats.map((stat, index) => (
                  <tr key={index}>
                    <td style={{ textTransform: 'capitalize' }}>{stat.referral_type}</td>
                    <td>{stat.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="admin-report-card">
          <h2>Users Below Threshold</h2>
          {usersBelowThreshold.length === 0 ? (
            <p className="no-data">No users below their credit threshold.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Credits</th>
                  <th>Credit Threshold</th>
                </tr>
              </thead>
              <tbody>
                {usersBelowThreshold.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.credits}</td>
                    <td>{user.credit_threshold}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-report-card">
          <h2>Top 5 Successful Lookups (Rolling 24 Hours)</h2>
          {topSuccessful24h.length === 0 ? (
            <p className="no-data">No successful lookups in the last 24 hours.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Successful Lookups</th>
                </tr>
              </thead>
              <tbody>
                {topSuccessful24h.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.successful_lookups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-report-card">
          <h2>Top 5 Successful Lookups (Rolling 7 Days)</h2>
          {topSuccessful7d.length === 0 ? (
            <p className="no-data">No successful lookups in the last 7 days.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Successful Lookups</th>
                </tr>
              </thead>
              <tbody>
                {topSuccessful7d.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.successful_lookups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-report-card">
          <h2>Top 5 Successful Lookups (All Time)</h2>
          {topSuccessfulAllTime.length === 0 ? (
            <p className="no-data">No successful lookups recorded.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Successful Lookups</th>
                </tr>
              </thead>
              <tbody>
                {topSuccessfulAllTime.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.successful_lookups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-report-card">
          <h2>Top 5 Failed Lookups (Rolling 24 Hours)</h2>
          {topFailed24h.length === 0 ? (
            <p className="no-data">No failed lookups in the last 24 hours.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Total Failed</th>
                  <th>Failure Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {topFailed24h.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.total_failed_lookups}</td>
                    <td>
                      {Object.entries(user.reasons).map(([reason, count]) => (
                        <div key={reason} style={{ fontSize: '0.9em', marginBottom: '2px' }}>
                          <strong>{reason}:</strong> {count}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="admin-report-card">
          <h2>Top 5 Failed Lookups (Rolling 7 Days)</h2>
          {topFailed7d.length === 0 ? (
            <p className="no-data">No failed lookups in the last 7 days.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Total Failed</th>
                  <th>Failure Breakdown</th>
                </tr>
              </thead>
              <tbody>
                {topFailed7d.map((user, index) => (
                  <tr key={index}>
                    <td>{user.email}</td>
                    <td>{user.total_failed_lookups}</td>
                    <td>
                      {Object.entries(user.reasons).map(([reason, count]) => (
                        <div key={reason} style={{ fontSize: '0.9em', marginBottom: '2px' }}>
                          <strong>{reason}:</strong> {count}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard; 